"use client";

import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { useActionState } from "react";
import { useFormStatus } from "react-dom";
import { submitContactForm, type ContactFormState } from "../../contact-actions";

const initialState: ContactFormState = { status: "idle", message: "" };
const MESSAGE_LIMIT = 1500;
/** Threshold at which the character counter shifts to a warning color. */
const COUNTER_WARN_AT = Math.floor(MESSAGE_LIMIT * 0.9);

/** Topic values that the dropdown actually offers. Anything else from
 *  the URL falls back to the empty placeholder so the form still
 *  requires an explicit pick. */
const VALID_TOPICS = new Set([
  "support",
  "bug",
  "credits",
  "partnership",
  "feature",
  "full-audit",
  "other",
]);

type FieldName = "name" | "email" | "topic" | "message";

export function ContactForm() {
  const [state, formAction] = useActionState(submitContactForm, initialState);
  // All fields are controlled. React 19 auto-resets uncontrolled fields in a
  // form with a server action after every submit — including on validation
  // failure — which would wipe the name/email/topic the user already filled.
  // Controlled state survives that reset; we clear it ourselves only on a
  // successful send (see the effect below).
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const formRef = useRef<HTMLFormElement>(null);

  // Preselect the topic when the contact page is reached with a
  // ?topic= query param (e.g. the /updates Roadmap CTA passes
  // ?topic=feature). Only honours values we actually offer in the
  // dropdown — unknown values fall back to the empty placeholder so
  // the user still picks explicitly.
  const searchParams = useSearchParams();
  const topicParam = searchParams?.get("topic");
  const initialTopic =
    topicParam && VALID_TOPICS.has(topicParam) ? topicParam : "";
  const [topic, setTopic] = useState(initialTopic);

  // When the "Request a full audit" CTA on an audit links here with
  // ?audit=<id>, prefill the message with a link back to that audit so
  // the request arrives knowing exactly which site and score to review.
  // Done in an effect (not useState init) so we can read window.origin
  // without tripping SSR, and only when the user hasn't typed yet.
  const auditParam = searchParams?.get("audit");
  useEffect(() => {
    if (!auditParam) return;
    setMessage((current) => {
      if (current) return current;
      const auditUrl = `${window.location.origin}/audit/${encodeURIComponent(
        auditParam,
      )}`;
      return `I'd like a full UX audit. Here's my Snap Site first pass: ${auditUrl}\n\n`;
    });
  }, [auditParam]);

  // Clear every field on success so the user can immediately send a second
  // message. We reset the controlled state directly (not formRef.reset(),
  // which only touches the DOM and would leave the controlled values stale).
  useEffect(() => {
    if (state.status !== "success") return;
    setName("");
    setEmail("");
    setTopic("");
    setMessage("");
  }, [state.status, state.message]);

  const fieldError = (field: FieldName): string | undefined =>
    state.fieldErrors?.[field];

  const fieldProps = (field: FieldName) => {
    const error = fieldError(field);
    return {
      "aria-invalid": error ? (true as const) : undefined,
      "aria-describedby": error ? `${field}-error` : undefined,
    };
  };

  const counterClass =
    message.length >= MESSAGE_LIMIT
      ? "contact-form-counter contact-form-counter-limit"
      : message.length >= COUNTER_WARN_AT
        ? "contact-form-counter contact-form-counter-warn"
        : "contact-form-counter";

  return (
    <form className="contact-form" action={formAction} ref={formRef} noValidate>
      <input
        className="hidden-field"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />

      <label className={fieldError("name") ? "has-error" : undefined}>
        <span>
          Name <em>Required</em>
        </span>
        <input
          name="name"
          placeholder="Your name"
          autoComplete="name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
          {...fieldProps("name")}
        />
        <FieldError field="name" message={fieldError("name")} />
      </label>

      <label className={fieldError("email") ? "has-error" : undefined}>
        <span>
          Email <em>Required</em>
        </span>
        <input
          name="email"
          type="email"
          placeholder="you@example.com"
          autoComplete="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
          {...fieldProps("email")}
        />
        <FieldError field="email" message={fieldError("email")} />
      </label>

      <label
        className={`full-field${fieldError("topic") ? " has-error" : ""}`}
      >
        <span>
          Topic <em>Required</em>
        </span>
        <select
          name="topic"
          value={topic}
          onChange={(event) => setTopic(event.target.value)}
          required
          {...fieldProps("topic")}
        >
          <option value="" disabled>
            Pick the closest match — Other works too.
          </option>
          <option value="support">Support</option>
          <option value="bug">Something isn&rsquo;t working</option>
          <option value="credits">Credits or pricing</option>
          <option value="partnership">Partnership</option>
          <option value="feature">Feature idea</option>
          <option value="full-audit">Full UX audit (paid)</option>
          <option value="other">Other</option>
        </select>
        <FieldError field="topic" message={fieldError("topic")} />
      </label>

      <label
        className={`full-field${fieldError("message") ? " has-error" : ""}`}
      >
        <span>
          Message <em>Required</em>
        </span>
        <textarea
          name="message"
          placeholder="Share the page, audit ID, or context we should look at."
          maxLength={MESSAGE_LIMIT}
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          required
          {...fieldProps("message")}
        />
        <small className={counterClass}>
          {message.length}/{MESSAGE_LIMIT} characters
        </small>
        <FieldError field="message" message={fieldError("message")} />
      </label>

      <p className="contact-form-privacy">
        We&rsquo;ll only use your email to reply to this message. See our{" "}
        <Link href="/privacy">privacy policy</Link> for the full details.
      </p>

      <SubmitButton
        label="Send message"
        pendingLabel="Sending message..."
      />

      <FormStatusMessage state={state} />
    </form>
  );
}

function FieldError({
  field,
  message,
}: {
  field: FieldName;
  message: string | undefined;
}) {
  if (!message) return null;
  return (
    <small id={`${field}-error`} className="contact-form-field-error">
      {message}
    </small>
  );
}

function SubmitButton({
  label,
  pendingLabel,
}: {
  label: string;
  pendingLabel: string;
}) {
  const { pending } = useFormStatus();
  return (
    <button
      className="button button-primary"
      type="submit"
      disabled={pending}
    >
      {pending ? pendingLabel : label}
    </button>
  );
}

function FormStatusMessage({ state }: { state: ContactFormState }) {
  if (state.status === "idle" || !state.message) return null;
  return (
    <p
      className={`form-status form-status-${state.status}`}
      role="status"
      aria-live="polite"
    >
      <span>{state.message}</span>
      {state.fallbackEmail ? (
        <>
          {" "}
          <a
            href={`mailto:${state.fallbackEmail}?subject=Snap%20Site%20contact`}
            className="form-status-fallback"
          >
            {state.fallbackEmail}
          </a>
        </>
      ) : null}
    </p>
  );
}
